import { memo, useCallback, useMemo, useState } from 'react';
import { js_beautify } from 'js-beautify';
import { Box, CircularProgress, Fab, IconButton, useTheme } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { IOrganization } from '@tymely/atoms';
import { useDocumentTitle, useOrganizationsQuery, useUser } from '@tymely/services';
import { CellTypography } from '@tymely/components';
import AddIcon from '@mui/icons-material/Add';

import { NewOrgModal } from './NewOrgModal';
import { OrgConfigModal } from './OrgConfigModal';

export const OrganizationEditor = memo(() => {
    const theme = useTheme();
    const organizationsQuery = useOrganizationsQuery(false);
    const [editingOrgConfig, setEditingOrgConfig] = useState<IOrganization>();
    const [newOrgModalOpen, setNewOrgModalOpen] = useState(false);

    useDocumentTitle('Organization Editor');

    const data = useMemo<IOrganization[]>(() => {
        return (organizationsQuery.data ?? [])
            .sort((org1, org2) => org1.id - org2.id)
            .map((org) => ({ ...org, uiId: `${org.id}` }));
    }, [organizationsQuery.data]);

    const RowActions = useCallback(function RowActions({ org }: { org: IOrganization }) {
        return (
            <Box sx={{ whiteSpace: 'nowrap' }}>
                <IconButton title="Edit organization" onClick={() => setEditingOrgConfig(org)}>
                    <EditIcon />
                </IconButton>
            </Box>
        );
    }, []);

    const table = useMaterialReactTable<IOrganization>({
        data: data || [],
        state: {
            density: 'compact',
        },
        initialState: { showColumnFilters: true },
        renderEmptyRowsFallback: organizationsQuery.data ? undefined : () => <CircularProgress />,
        enableTopToolbar: false,
        enablePagination: false,
        enableBottomToolbar: false,
        enableStickyHeader: true,
        enableRowActions: true,
        renderRowActions: ({ row: { original: org } }) => <RowActions org={org} />,
        enableColumnActions: false,
        displayColumnDefOptions: {
            'mrt-row-actions': {
                size: 52,
                grow: false,
                Header: () => '',
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: '100%',
            },
        },
        columns: [
            {
                header: 'ID',
                accessorKey: 'id',
            },
            {
                header: 'Name',
                accessorKey: 'name',
            },
            {
                header: 'Live?',
                accessorKey: 'is_live_client',
                filterVariant: 'select',
                filterSelectOptions: [
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                ],
                Cell: ({ cell }) => <CellTypography>{cell.getValue() ? 'Yes' : 'No'}</CellTypography>,
            },
            {
                header: 'Disabled?',
                accessorKey: 'disabled',
                filterVariant: 'select',
                filterSelectOptions: [
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                ],
                Cell: ({ cell }) => <CellTypography>{cell.getValue() ? 'Yes' : 'No'}</CellTypography>,
            },
            {
                header: 'Active?',
                accessorKey: 'is_active',
                filterVariant: 'select',
                filterSelectOptions: [
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                ],
                Cell: ({ cell }) => <CellTypography>{cell.getValue() ? 'Yes' : 'No'}</CellTypography>,
            },
            {
                header: 'Client Name',
                accessorKey: 'client_name',
            },
            {
                header: 'Config',
                accessorFn: (row) => (row.config ? js_beautify(JSON.stringify(row.config)) : null),
                muiTableBodyCellProps: {
                    style: {
                        padding: 0,
                    },
                },
                filterFn: 'contains',
                Cell: ({ cell }) => {
                    const json = cell.getValue();
                    if (!json) return null;
                    return (
                        <CellTypography
                            wrap
                            dangerouslySetInnerHTML={{ __html: json }}
                            sx={{
                                display: 'block',
                                maxHeight: '160px !important',
                                overflow: 'scroll',
                            }}
                        />
                    );
                },
            },
        ],
    });

    const user = useUser();

    if (!user?.isAdmin) {
        return <div>Access denied</div>;
    }

    return (
        <Box width={1} display="flex" justifyContent="center" sx={{ height: '100%' }}>
            <MaterialReactTable table={table} />
            {editingOrgConfig && (
                <OrgConfigModal
                    orgName={editingOrgConfig.name}
                    orgId={editingOrgConfig.id}
                    open={!!editingOrgConfig}
                    onClose={() => setEditingOrgConfig(undefined)}
                />
            )}
            {newOrgModalOpen && (
                <NewOrgModal open onClose={() => setNewOrgModalOpen(false)} onSuccess={setEditingOrgConfig} />
            )}
            {!organizationsQuery.isLoading && (
                <Fab
                    color="primary"
                    aria-label="add"
                    onClick={() => setNewOrgModalOpen(true)}
                    sx={{
                        position: 'absolute',
                        bottom: theme.spacing(4),
                        right: theme.spacing(4),
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
});

OrganizationEditor.displayName = 'OrganizationEditor';
